// Shared client helpers + types for the asset-folder system, so the sidebar
// panel and the node "Save asset" submenu don't duplicate fetch/normalize logic.

export type AssetFolder = {
  id: string;
  name: string;
  parentId: string | null;
};

export type AssetFolderScope = 'personal' | 'team';

/** Fetch the flat folder list for a scope and normalize ids/parent ids. */
export async function fetchAssetFolders(scope: AssetFolderScope): Promise<AssetFolder[]> {
  const res = await fetch(`/api/asset-folders?scope=${scope}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !Array.isArray(data.items)) return [];
  return data.items.map((f: any) => ({
    id: String(f.id),
    name: String(f.name ?? ''),
    parentId: f.parentId ? String(f.parentId) : null,
  }));
}

/** Group folders by parent id (children sorted by name) for tree rendering. */
export function groupFoldersByParent<T extends AssetFolder>(
  folders: T[],
): Map<string | null, T[]> {
  const byParent = new Map<string | null, T[]>();
  for (const f of folders) {
    const key = f.parentId ?? null;
    const list = byParent.get(key) ?? [];
    list.push(f);
    byParent.set(key, list);
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => a.name.localeCompare(b.name));
  }
  return byParent;
}

/**
 * Collect a folder id plus all of its descendants (iterative DFS). Shared by the
 * optimistic delete in the panel and the recursive delete on the server.
 */
export function collectFolderSubtree<T extends AssetFolder>(
  folders: T[],
  rootId: string,
): Set<string> {
  const byParent = groupFoldersByParent(folders);
  const removed = new Set<string>();
  const stack = [rootId];
  let guard = 0;
  while (stack.length && guard++ < 10000) {
    const cur = stack.pop() as string;
    if (removed.has(cur)) continue;
    removed.add(cur);
    for (const child of byParent.get(cur) ?? []) stack.push(child.id);
  }
  return removed;
}
